import React, { useState } from 'react'

import { Link as MuiLink, Typography } from '@mui/material'
import CustomerCodeStatus from 'features/customers/components/CustomerCodeStatus'
import CustomerEstimatedCompanies from 'features/customers/components/details/CustomerEstimatedCompanies'
import CustomerSubAccount from 'features/customers/components/details/CustomerSubAccount'
import CustomerUsageOverview from 'features/customers/components/details/CustomerUsageOverview'
import PlanBadge from 'features/customers/components/PlanBadge'
import { DETAILS_PANELS, type DetailsPanelId } from 'features/customers/constants/detailsPanels'
import { subscriptionStatus } from 'features/customers/constants/subscriptionStatus'
import type { CustomerDetail } from 'features/customers/hooks/useCustomer'

import { Flag, LabelWithTooltip, SectionPanel, StatusChip, YesNo } from 'components/_base'
import { DescriptionListTable } from 'components/table'
import type { ColumnConfig } from 'components/table/types'
import { findCountryByCode } from 'utils/countries'
import { formatAccountDate } from 'utils/date'

const withProtocol = (website: string) => (/^https?:\/\//i.test(website) ? website : `https://${website}`)

export const identity = [
  { title: 'Name', field: 'name' },
  {
    title: 'Website',
    render: ({ website }: CustomerDetail) =>
      website ? (
        <MuiLink href={withProtocol(website)} rel="noreferrer noopener" target="_blank">
          {website}
        </MuiLink>
      ) : (
        '—'
      ),
  },
  { title: 'Code status', render: CustomerCodeStatus },
  { title: 'Customer ID', field: 'id' },
  { title: 'Client ID', field: 'client_id' },
  {
    title: 'Country',
    render: ({ country }: CustomerDetail) =>
      country ? (
        <>
          <Flag country={country} /> {findCountryByCode(country)?.printableName || country} ({country})
        </>
      ) : (
        '—'
      ),
  },
]

export const plan = [
  {
    title: 'Plan',
    render: ({ plan: customerPlan }: CustomerDetail) => <PlanBadge name={customerPlan?.name} />,
  },
  {
    title: 'Stripe Subscription',
    render: ({ stripe_subscription_status }: CustomerDetail) => (
      <StatusChip {...subscriptionStatus(stripe_subscription_status)} />
    ),
  },
  {
    title: (
      <LabelWithTooltip
        label="Tier"
        tooltip='Determines the base (plan level) limits for leads, contacts, and phone numbers. This value should match the tier level of the subscription in Stripe. Tier 9 is used for legacy \"unlimited\" customers (500k leads). Valid values: 1-9.'
      />
    ),
    field: 'tier',
  },
  {
    title: (
      <LabelWithTooltip
        label="Estimated companies / month"
        tooltip="The same estimate the customer sees in Website Visitors: how many companies we expect to identify per month, extrapolated from their recent traffic. A dash means there is not enough traffic to estimate from yet."
      />
    ),
    render: ({ plan: customerPlan }: CustomerDetail) => <CustomerEstimatedCompanies planName={customerPlan?.name} />,
  },
]

export const flags = [
  { title: 'Invoice / Partner', render: ({ is_partner }: CustomerDetail) => <YesNo value={is_partner} /> },
  { title: 'Beta', render: ({ is_beta }: CustomerDetail) => <YesNo value={is_beta} /> },
  {
    title: (
      <LabelWithTooltip
        label="Sales-led (SLG)"
        tooltip="Sold by sales rather than self-serve signup. Affects which trial and checkout flows they see."
      />
    ),
    render: ({ is_sales_led }: CustomerDetail) => <YesNo value={is_sales_led} />,
  },
  { title: 'Master Account', render: ({ master }: CustomerDetail) => <YesNo value={!!master} /> },
  { title: 'Sub Account', render: CustomerSubAccount },
]

export const timeline = (customer: Partial<CustomerDetail>) => [
  { title: 'Created', render: ({ created_at }: CustomerDetail) => formatAccountDate(created_at) },
  { title: 'Updated', render: ({ updated_at }: CustomerDetail) => formatAccountDate(updated_at) },
  ...(customer.deleted_at
    ? [
        {
          title: 'Deleted',
          render: ({ deleted_at }: CustomerDetail) => (
            <Typography color="error.main" component="span" variant="body2">
              {formatAccountDate(deleted_at)}
            </Typography>
          ),
        },
      ]
    : []),
]

type CustomerDetailsSummaryProps = {
  customer: Partial<CustomerDetail>
}

const CustomerDetailsSummary = ({ customer }: CustomerDetailsSummaryProps) => {
  const [shut, setShut] = useState<Record<string, boolean>>({})

  const facts = (config: ColumnConfig<CustomerDetail>[]) => (
    <DescriptionListTable config={config} data={customer} titleCellStyle={{ width: '45%' }} />
  )

  const bodies: Record<Exclude<DetailsPanelId, 'ai'>, React.ReactNode> = {
    identity: facts(identity),
    plan: facts(plan),
    usage: <CustomerUsageOverview />,
    flags: facts(flags),
    timeline: facts(timeline(customer)),
  }

  return (
    <>
      {Object.entries(bodies).map(([id, body]) => {
        const { title, icon, color } = DETAILS_PANELS[id as DetailsPanelId]

        return (
          <SectionPanel
            color={color}
            icon={icon}
            key={id}
            onToggle={() => setShut(current => ({ ...current, [id]: !current[id] }))}
            open={!shut[id]}
            title={title}>
            {body}
          </SectionPanel>
        )
      })}
    </>
  )
}

export default CustomerDetailsSummary
